import { getDoc, setDoc } from './store';
import type { AbaId, Block, Box, Item, Task } from './types';
import { uid } from '../lib/date';
import { t } from '../lib/i18n';
import { oferecerDesfazer } from '../lib/desfazer';

const upd = (fn: (d: { quick: any[]; boxes: Box[] }) => any) => setDoc('anotacoes', d => ({ ...d, ...fn(d) }));

export const addQuick = (text: string) => upd(d => ({ quick: [...d.quick, { id: uid(), text: text.trim(), done: false }] }));
export const toggleQuick = (id: string) => upd(d => ({ quick: d.quick.map(i => (i.id === id ? { ...i, done: !i.done } : i)) }));
export const removeQuick = (id: string) => upd(d => ({ quick: d.quick.filter(i => i.id !== id) }));
export const clearQuickDone = () => upd(d => ({ quick: d.quick.filter(i => !i.done) }));
/** Cópia de um item da Lista rápida, logo abaixo do original (0.65). */
export const duplicarQuick = (id: string) => upd(d => {
  const i = d.quick.findIndex(x => x.id === id); if (i < 0) return {};
  const o = d.quick[i];
  const quick = [...d.quick]; quick.splice(i + 1, 0, { ...o, id: uid(), text: t('{titulo} (cópia)', { titulo: o.text }), done: false });
  return { quick };
});

export const novoBloco = (type: Block['type']): Block => ({ id: uid(), type, items: type === 'text' || type === 'desenho' || type === 'sub' ? [] : [{ id: uid(), text: '', done: false }], text: '' });

export function addBox(title = '', aba: AbaId = 'anotacoes', ref?: string) {
  const b: Box = { id: uid(), title, aba, blocks: [novoBloco('check')], createdAt: new Date().toISOString(), ...(ref ? { ref } : {}) };
  upd(d => ({ boxes: [b, ...d.boxes] }));
  return b.id;
}
/** Caixa fixa embutida na Lista rápida (0.47): os blocos de checklist, lista, texto livre e desenho que ficam abaixo dos itens rápidos. */
export const ID_RAPIDA = 'lista-rapida';
export function caixaRapida(): Box {
  const ja = getDoc('anotacoes').boxes.find(b => b.id === ID_RAPIDA);
  if (ja) return ja;
  const b: Box = { id: ID_RAPIDA, title: '', aba: 'anotacoes', blocks: [], createdAt: new Date().toISOString() };
  upd(d => (d.boxes.some(x => x.id === ID_RAPIDA) ? {} : { boxes: [...d.boxes, b] }));
  return b;
}
/** Guarda uma caixa pronta (vinda de outra aba, 0.52). */
export const inserirBox = (b: Box) => upd(d => ({ boxes: [b, ...d.boxes.filter(x => x.id !== b.id)] }));
/** Cópia de uma caixa, logo abaixo da original (0.52). */
export function duplicarBox(id: string) {
  upd(d => {
    const i = d.boxes.findIndex(b => b.id === id); if (i < 0) return {};
    const o = d.boxes[i];
    const c: Box = { ...o, id: uid(), title: o.title ? o.title + ' ' + t('(cópia)') : '', createdAt: new Date().toISOString(), archivedAt: null,
      blocks: o.blocks.map(b => ({ ...b, id: uid(), items: b.items.map(it => ({ ...it, id: uid() })) })) };
    const boxes = [...d.boxes]; boxes.splice(i + 1, 0, c);
    return { boxes };
  });
}
export const updateBox = (id: string, fn: (b: Box) => Box) => upd(d => ({ boxes: d.boxes.map(b => (b.id === id ? fn(b) : b)) }));
export const removeBox = (id: string) => upd(d => ({ boxes: d.boxes.filter(b => b.id !== id) }));
export const moveBox = (id: string, aba: AbaId) => updateBox(id, b => ({ ...b, aba, ref: null }));   // enviada para outra aba, deixa de ser card da ficha
export const archiveBox = (id: string) => { updateBox(id, b => ({ ...b, archivedAt: new Date().toISOString() })); oferecerDesfazer('arquivar', () => unarchiveBox(id)); };   // 0.67: Desfazer por 5 s
export const unarchiveBox = (id: string) => updateBox(id, b => ({ ...b, archivedAt: null }));
export const ativa = (b: Box) => !b.archivedAt;

/** Caixa vira tarefa: título vira o nome, itens viram subtarefas e o texto livre vira anotações. */
export function boxToTask(b: Box): Partial<Task> {
  const subs = b.blocks.flatMap(bl => (bl.type === 'text' ? [] : bl.items)).filter(i => i.text.trim()).map(i => ({ id: uid(), text: i.text.trim() }));
  const notes = b.blocks.filter(bl => bl.type === 'text' && bl.text.trim()).map(bl => bl.text.trim()).join('\n\n');
  return { title: b.title.trim() || t('Anotação'), subtasks: subs, notes };
}

// ---------- arrastar para reorganizar (0.34) ----------
export const ordenarQuick = (novos: Item[]) => upd(() => ({ quick: novos }));
/** Nova ordem de um pedaço das caixas (as desta aba), mantendo as outras no lugar. */
export function ordenarCaixas(parte: Box[]) {
  upd(d => {
    const ids = new Set(parte.map(b => b.id));
    const fila = [...parte];
    return { boxes: d.boxes.map(b => (ids.has(b.id) ? fila.shift()! : b)) };
  });
}
/** Leva um item (checklist ou lista) de um bloco para outro, na mesma caixa ou em outra. destino = "caixa:bloco" ou "box:caixa". */
/** Texto 27 (A2/A3): o grupo de cada bloco: o id do subtítulo de cima ('' = antes do 1º subtítulo). */
export function grupoDosBlocos(blocks: Block[]): string[] {
  let g = '';
  return blocks.map(bl => (bl.type === 'sub' ? (g = bl.id) : g));
}
/** Texto 27 (A3): leva itens (de uma ou mais caixas) para o grupo de uma caixa, no fim da 1ª lista/checklist do grupo
 *  (se o grupo não tiver nenhuma, cria um checklist no fim dele). grupo: id do subtítulo, '' = antes do 1º subtítulo.
 *  O item mantém a marcação (feito / não feito). */
export function moverItensPara(itens: { caixa: string; bloco: string; item: string }[], paraCaixa: string, grupo: string) {
  upd(d => {
    const pegos: Item[] = [];
    let boxes = d.boxes.map(b => {
      const meus = itens.filter(x => x.caixa === b.id);
      if (!meus.length) return b;
      const blocks = b.blocks.map(bl => {
        const sai = meus.filter(x => x.bloco === bl.id).map(x => x.item);
        if (!sai.length) return bl;
        pegos.push(...bl.items.filter(i => sai.includes(i.id)));
        return { ...bl, items: bl.items.filter(i => !sai.includes(i.id)) };
      }).filter(bl => bl.type === 'text' || bl.type === 'desenho' || bl.type === 'sub' || bl.items.length);
      return { ...b, blocks: blocks.length ? blocks : [novoBloco('check')] };
    });
    if (!pegos.length) return {};
    boxes = boxes.map(b => {
      if (b.id !== paraCaixa) return b;
      const gs = grupoDosBlocos(b.blocks);
      const idx = b.blocks.findIndex((bl, i) => gs[i] === grupo && (bl.type === 'check' || bl.type === 'list'));
      if (idx >= 0) return { ...b, blocks: b.blocks.map((bl, i) => (i === idx ? { ...bl, items: [...bl.items.filter(x => x.text.trim()), ...pegos] } : bl)) };
      let fim = -1; gs.forEach((g, i) => { if (g === grupo) fim = i; });
      const novo = { ...novoBloco('check'), items: pegos };
      const blocks = [...b.blocks]; blocks.splice(fim + 1, 0, novo);
      return { ...b, blocks };
    });
    return { boxes };
  });
}

export function moverItemCaixa(deCaixa: string, deBloco: string, itemId: string, destino: string) {
  // texto 27 (A3): soltar em cima de um subtítulo (mesmo recolhido) = entra no fim daquele grupo
  if (destino.startsWith('sub:')) { const [, cx, sub] = destino.split(':'); return moverItensPara([{ caixa: deCaixa, bloco: deBloco, item: itemId }], cx, sub); }
  upd(d => {
    const orig = d.boxes.find(b => b.id === deCaixa);
    const item = orig?.blocks.find(bl => bl.id === deBloco)?.items.find(i => i.id === itemId);
    if (!item) return {};
    let [paraCaixa, paraBloco] = destino.startsWith('box:') ? [destino.slice(4), ''] : destino.split(':');
    const alvo = d.boxes.find(b => b.id === paraCaixa);
    if (!alvo) return {};
    if (!paraBloco) paraBloco = alvo.blocks.find(bl => bl.type === 'check' || bl.type === 'list')?.id ?? '';
    const novoBl = paraBloco ? null : { ...novoBloco('check'), items: [] as Item[] };
    const boxes = d.boxes.map(b => {
      let bs = b.blocks;
      if (b.id === deCaixa) bs = bs.map(bl => (bl.id === deBloco ? { ...bl, items: bl.items.filter(i => i.id !== itemId) } : bl))
        .filter(bl => bl.type === 'text' || bl.type === 'desenho' || bl.type === 'sub' || bl.items.length || bs.length === 1);
      if (b.id === paraCaixa) bs = novoBl ? [...bs, { ...novoBl, items: [item] }] : bs.map(bl => (bl.id === paraBloco ? { ...bl, items: [...bl.items.filter(i => i.text.trim() || i.id === itemId), item] } : bl));
      return bs === b.blocks ? b : { ...b, blocks: bs.length ? bs : [novoBloco('check')] };
    });
    return { boxes };
  });
}

/** Texto 29 (REGRAS 3.4): leva blocos inteiros (Texto livre, Lista, Checklist, Desenho) para o fim de um grupo de uma caixa.
 *  grupo: id do subtítulo, '' = antes do 1º subtítulo. A caixa de origem nunca fica sem nenhum bloco. */
export function moverBlocosPara(blocos: { caixa: string; bloco: string }[], paraCaixa: string, grupo: string) {
  upd(d => {
    const pegos: Block[] = [];
    let boxes = d.boxes.map(b => {
      const sai = blocos.filter(x => x.caixa === b.id).map(x => x.bloco);
      if (!sai.length) return b;
      pegos.push(...b.blocks.filter(bl => sai.includes(bl.id) && bl.type !== 'sub'));
      const resto = b.blocks.filter(bl => !sai.includes(bl.id) || bl.type === 'sub');
      return { ...b, blocks: resto.length ? resto : [novoBloco('check')] };
    });
    if (!pegos.length) return {};
    boxes = boxes.map(b => {
      if (b.id !== paraCaixa) return b;
      const blocks = [...b.blocks];
      blocks.splice(fimDoGrupo(blocks, grupo) + 1, 0, ...pegos);
      return { ...b, blocks };
    });
    return { boxes };
  });
}
/** Índice do último bloco de um grupo ('' = antes do 1º subtítulo; o subtítulo em si conta, para grupo vazio). */
export function fimDoGrupo(blocks: Block[], grupo: string): number {
  const gs = grupoDosBlocos(blocks);
  let fim = grupo ? blocks.findIndex(bl => bl.id === grupo) : -1;
  gs.forEach((g, i) => { if (g === grupo) fim = i; });
  if (!grupo && fim < 0) { const p = blocks.findIndex(bl => bl.type === 'sub'); fim = p < 0 ? blocks.length - 1 : p - 1; }
  return fim;
}
